import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CatBadge } from "blocks-nextjs/blocks-react/components/Badge/Badge";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatContextualMenu } from "blocks-nextjs/blocks-react/components/ContextualMenu/ContextualMenu";
import { CatIconAddCircle } from "blocks-nextjs/blocks-react/components/Icons/AddCircle/AddCircle";
import { CatIconChevronDown } from "blocks-nextjs/blocks-react/components/Icons/ChevronDown/ChevronDown";
import { CatIconSuccessFilled } from "blocks-nextjs/blocks-react/components/Icons/SuccessFilled/SuccessFilled";
import { CatList } from "blocks-nextjs/blocks-react/components/List/List";
import { CatListItem } from "blocks-nextjs/blocks-react/components/ListItem/ListItem";
import { CatSearchForm } from "blocks-nextjs/blocks-react/components/SearchForm/SearchForm";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAssetImageIcon } from "../../pages/Projects/ProjectDetails.helper";
import { useSearchAssetsQuery } from "../../services/query/assets-v2.query";
import { useGetProjectDetailsQuery } from "../../services/query/create-project-api.query";
import useGlobalStore from "../../store/useGlobalStore";
import useNewProjectStore from "../../store/useNewProjectStore";
import { FilterType } from "../../swagger/assets-v2/models/FilterType";
export var AssetType;
(function (AssetType) {
    AssetType["ESS"] = "ESS";
    AssetType["MACHINE"] = "MACHINE";
})(AssetType || (AssetType = {}));
export const AddAssetsListCard = ({ setAddedAssetListInfo }) => {
    const [sortOrder, setSortOrder] = useState("ASC");
    const [searchTerm, setSearchTerm] = useState("");
    const [addedAssetList, setAddedAssetList] = useState([]);
    const { primaryPartyType, userPartyNumber } = useGlobalStore((state) => ({
        primaryPartyType: state.primaryPartyType,
        userPartyNumber: state.userPartyNumber
    }));
    const { projectId } = useNewProjectStore((state) => ({
        projectId: state.projectId
    }));
    const queryParams = {
        ...(userPartyNumber && primaryPartyType !== "CAT" && { partyNumber: userPartyNumber }),
        ...(userPartyNumber && primaryPartyType === "CAT" && { downSelectPartyNumber: userPartyNumber })
    };
    const { data: projectDetailsResponse, refetch: fetchProjectDetails } = useGetProjectDetailsQuery(projectId, queryParams);
    useEffect(() => {
        if (projectId) {
            fetchProjectDetails();
        }
    }, [projectId, fetchProjectDetails]);
    useEffect(() => {
        if (projectDetailsResponse?.assetDetails) {
            const existingAssets = projectDetailsResponse.assetDetails.map((asset) => ({
                assetId: asset.assetId,
                assetMetadata: {
                    model: asset.assetMetadata?.model,
                    modelYear: asset.assetMetadata?.modelYear,
                    productFamily: {
                        code: asset.assetMetadata?.productFamily?.code,
                        iconUrl: asset.assetMetadata?.productFamily?.iconUrl,
                        name: asset.assetMetadata?.productFamily?.name
                    }
                },
                assetName: asset.assetName,
                assetSerialNumber: asset.assetSerialNumber ?? "",
                assetSourceId: asset.assetSourceId,
                assetSourceType: asset.assetSourceType?.toUpperCase()
            }));
            setAddedAssetList(existingAssets);
        }
    }, [projectDetailsResponse]);
    const isSearchEnabled = searchTerm.trim().length >= 3;
    const requestBody = isSearchEnabled
        ? {
            filters: [
                {
                    isCaseSensitive: false,
                    propertyName: "serialNumber",
                    type: FilterType.CONTAINS,
                    value: searchTerm.trim()
                }
            ],
            logicalExpression: "$0"
        }
        : undefined;
    const { data: machineAssetList } = useSearchAssetsQuery({
        enabled: isSearchEnabled,
        paramsList: {
            ...(userPartyNumber && primaryPartyType === "CAT" && { downSelectPartyNumber: userPartyNumber })
        },
        requestBody
    });
    const preparedMachineAssetList = useMemo(() => {
        if (!isSearchEnabled || !machineAssetList?.assets) {
            return addedAssetList;
        }
        return machineAssetList.assets.map((asset) => ({
            assetMetadata: {
                model: asset.metadata?.model,
                modelYear: asset.metadata?.modelYear,
                productFamily: {
                    code: asset.metadata?.productFamily?.code,
                    iconUrl: asset.metadata?.icon?.imageUrl,
                    name: asset.metadata?.productFamily?.name
                }
            },
            assetName: asset.metadata?.name ?? "",
            assetSerialNumber: asset.metadata?.serialNumber ?? "",
            assetSourceId: asset.id,
            assetSourceType: AssetType.MACHINE
        }));
    }, [isSearchEnabled, machineAssetList, addedAssetList]);
    useEffect(() => {
        setAddedAssetListInfo(addedAssetList);
    }, [addedAssetList, setAddedAssetListInfo]);
    const handleSearch = (term) => {
        setSearchTerm(term ?? "");
    };
    const listRef = useRef(null);
    const handleScroll = useCallback(() => {
        const el = listRef.current;
        if (!el)
            return;
        const { clientHeight, scrollHeight, scrollTop } = el;
        if (scrollHeight - scrollTop - clientHeight <= 1) {
            // Implement infinite scroll logic here if needed
            return;
        }
    }, []);
    const getSortOptions = () => {
        return (_jsx("div", { children: _jsx(CatContextualMenu, { position: "bottom", children: _jsxs(React.Fragment, { children: [_jsxs(CatButton, { hideText: true, size: "xs", slot: "trigger", variant: "ghost", children: ["Menu", _jsx(CatIconChevronDown, { slot: "after" })] }), _jsxs(CatList, { className: "w-44", "data-test": "sort-assets-list", onBlSelect: (e) => {
                                const itemInfo = e.detail.value;
                                setSortOrder(itemInfo.value);
                            }, children: [_jsx(CatListItem, { "data-test": "sort-assets-ascending", value: {
                                        label: "Ascending by S/N",
                                        value: "ASC"
                                    } }), _jsx(CatListItem, { "data-test": "sort-assets-descending", value: {
                                        label: "Descending by S/N",
                                        value: "DESC"
                                    } })] })] }, ".0") }) }));
    };
    const getSortName = () => {
        return sortOrder === "ASC" ? "S/N - A to Z " : "S/N - Z to A";
    };
    return (_jsxs("section", { className: "flex-1 flex flex-col gap-[16px] bg-[#FFFFFF] rounded-[4px] border-[#CCCCCC] border py-[16px]", children: [_jsx("p", { className: "font-semibold text-sm leading-5 tracking-normal px-[16px]", children: "Available assets" }), _jsx("div", { className: "px-[12px]", children: _jsx(CatSearchForm, { arialListLabel: "Search", "data-test": "search-asset-serial-number", isEmpty: true, label: "Label", onBlChange: (e) => {
                        handleSearch(e.detail.value);
                    }, placeholder: "Add Machine Asset Serial #" }) }), _jsxs("section", { children: [_jsxs("header", { className: "flex justify-between items-center h-[32px] px-[12px]  border-b-[0.2px] border-[#CCCCCC]", children: [_jsxs("div", { className: "flex items-center", children: [_jsx("span", { className: "font-semibold text-sm leading-5 tracking-normal", children: `Sort By ${getSortName()}` }), _jsx("span", { children: getSortOptions() })] }), _jsx("div", { children: _jsx(CatBadge, { "data-test": "assets-count-badge", text: String(preparedMachineAssetList.length), variant: "blue" }) })] }), _jsx("ul", { className: "flex flex-col max-h-[230px] h-[230px] overflow-y-auto", onScroll: handleScroll, ref: listRef, children: preparedMachineAssetList.map((i, index) => (_jsxs("li", { className: "flex items-center gap-[16px] min-h-[64px] px-[16px] py-[8px] border-b-[0.2px] border-[#CCCCCC] last:border-0", children: [_jsx("figure", { "aria-hidden": "true", children: _jsx("img", { alt: "Assets", className: "w-[48px] min-h-[48px] min-w-[48px]", "data-test": `asset-${index}-image`, src: i.assetMetadata?.productFamily?.iconUrl
                                            ? i.assetMetadata.productFamily.iconUrl
                                            : getAssetImageIcon({ assetId: "5fe212ea-8fa8-4d71-a020-d3e4b504ee3amm" }) }) }), _jsxs("div", { className: "grow", children: [_jsxs("p", { className: "font-bold tracking-normal align-middle text-sm", "data-test": `asset-${index}-id`, children: [i.assetSourceId?.replace(/&pipe;$/g, "").replace(/&pipe;/g, " | "), " "] }), _jsx("p", { className: "font-normal text-xs leading-4 tracking-normal align-middle", "data-test": `asset-${index}-serial-number`, children: i.assetSerialNumber })] }), _jsx("div", { className: "asset-item__actions", children: _jsx(CatButton, { "data-test": `asset-${index}-button`, hideText: true, onClick: () => {
                                            setAddedAssetList((prev) => {
                                                const isMatch = (a) => a.assetSourceId === i.assetSourceId &&
                                                    a.assetSerialNumber === i.assetSerialNumber &&
                                                    a.assetSourceType === i.assetSourceType;
                                                const exists = prev.some(isMatch);
                                                if (exists) {
                                                    return prev.filter((a) => !isMatch(a));
                                                }
                                                return [
                                                    ...prev,
                                                    {
                                                        assetMetadata: i.assetMetadata,
                                                        assetName: i.assetName,
                                                        assetSerialNumber: i.assetSerialNumber,
                                                        assetSourceId: i.assetSourceId,
                                                        assetSourceType: i.assetSourceType
                                                    }
                                                ];
                                            });
                                        }, size: "sm", variant: "ghost", children: _jsxs(React.Fragment, { children: [addedAssetList.some((a) => a.assetSourceId === i.assetSourceId &&
                                                    a.assetSerialNumber === i.assetSerialNumber &&
                                                    a.assetSourceType === i.assetSourceType) ? (_jsx(CatIconSuccessFilled, { color: "information", iconTitle: "Remove asset", size: "lg", slot: "before" })) : (_jsx(CatIconAddCircle, { color: "information", iconTitle: "Asset added", size: "lg", slot: "before" })), addedAssetList.some((a) => a.assetSourceId === i.assetSourceId &&
                                                    a.assetSerialNumber === i.assetSerialNumber &&
                                                    a.assetSourceType === i.assetSourceType)
                                                    ? "Remove"
                                                    : "Add"] }, ".0") }) })] }, i.assetSourceId))) })] })] }));
};
export default AddAssetsListCard;
